/**
 * 외부 API(날씨 · 주소) 관리 — 관리 콘솔 (2026-09-21 · 시안 「외부 API 콘솔」).
 *
 * 서버 SSOT: `apps/chat-server/src/modules/admin/external-api.routes.ts` (SYSTEM_ADMIN 전용).
 * BFF 통로: `/api/admin/external-apis…` → `admin/backend` 의 catch-all(`/admin/` 허용) → chat-server `/api/v1/admin/…`.
 *   ★ 새 통로를 만들지 않았다 — `/system/…` 이었다면 허용 목록에 없어 404 였을 자리다(글꼴 등록이 겪은 그것).
 *
 * ## ★★★ 키는 «넣을 때만» 지나간다
 *
 * 응답에는 **평문 키가 없다** — 끝 네 자리(`key_hint`)와 상태(`key_state`)뿐이다.
 * 마스킹된 값을 그대로 보내면 서버가 `MASKED_VALUE` 로 **거절**한다(화면 결함이 멀쩡한 키를 망가뜨리지 않게).
 * 그래서 이 모듈에는 **키를 «읽는» 길이 없다.** 바꾸는 길은 새 키로 덮어쓰는 것뿐이다.
 */
import { apiClient } from './client';

/** ★ 2026-09-21 — `stt` 가 셋째다. 표는 그대로고 `category` 글자만 는다(마이그레이션 0). */
/** ⭐ 2026-10-22 — `diarize`(화자 구분)가 넷째다. 같은 표 · 같은 꼴(`diarize-adapters.ts`). `config` 읽기는 `diarizeEndpoint.ts`. */
export type ExternalApiCategory = 'weather' | 'address' | 'stt' | 'diarize';
/**
 * none = 아직 없음 · stored = 저장됨.
 *
 * ⚠ **`unreadable` 은 서버가 «더 이상 보내지 않는다»** (2026-09-24 확인 —
 * `lib/external-api/store.ts:75` 가 `'none' | 'stored'` 로 줄었다. 목록을 읽을 때마다 복호화하던 것을
 * 없애면서 그 판정을 만들 자리가 사라졌다).
 *
 * ⛔ 그래서 이 값을 그리는 화면 갈래는 **지금 닿지 않는다**(죽은 갈래다). 남겨 둔 까닭은
 * **대체 경로가 아직 없기 때문**이다 — 「키를 못 읽음」을 목록에서 보여 주려면 연결 시험이 그 결과를
 * `last_probe_status` 에 **남겨야** 하는데, 서버가 그 앞에서 돌아선다
 * (`modules/admin/external-api.routes.ts:470` 이 409 로 반환 · 기록은 `:486`).
 * ⇒ 서버가 그것을 남기게 되면 이 갈래를 `last_probe_status` 로 옮긴다. 관제에 올려 둔 물음이다.
 */
export type KeyState = 'none' | 'stored' | 'unreadable';
export type ProbeVerdict = 'ok' | 'auth_failed' | 'shared_quota_exhausted' | 'provider_error' | 'unreachable';

/** 종류별 사용량 — 한도가 오퍼레이션마다 따로다(기상청 실황·초단기·단기 각 10,000). */
export interface UsageByKind {
  kind: string;
  calls: number;
  quota_hits: number;
  errors: number;
  /** 공급자가 말한 값 — 헤더를 안 주는 공급자는 null(우리가 센 수만 보인다). */
  limit_seen: number | null;
  remaining_seen: number | null;
  remaining_seen_at: string | null;
}

export interface ExternalApiProvider {
  provider_key: string;
  category: ExternalApiCategory;
  label: string;
  enabled: boolean;
  sort_order: number;
  daily_quota: number | null;
  has_key: boolean;
  key_state: KeyState;
  /** `••••a1b2` — 끝 네 자리. 키가 없으면 null. */
  key_hint: string | null;
  last_probe_status: string | null;
  last_probe_at: string | null;
  last_429_at: string | null;
  updated_at: string;
  adapter_available: boolean;
  /** DB 에 키가 없고 서버 환경변수의 «개발용» 키로 도는 중. 운영에서는 보이면 안 된다. */
  env_key_fallback: boolean;
  /** 오늘 기록이 없으면 null — 「0회」와 다르다. */
  usage_today: { calls: number; quota_hits: number; errors: number; by_kind: UsageByKind[] } | null;
  /**
   * 범주마다 다른 칸. ✅ **서버가 «늘» 준다**(2026-09-24 확인 — `store.ts:137` `config: r.config ?? {}`).
   * ⚠ 그래도 선택 칸으로 둔다 — 낡은 응답을 물고 있는 탭이 있을 수 있고, 읽는 쪽이 기본값을 만든다.
   * ⛔ 여기에 키가 들어가지 않는다 — 키는 암호문 세 칸에만 있다(서버가 키 류 이름을 400 으로 막는다).
   */
  config?: Record<string, unknown> | null;
}

/** STT 가 쓰는 `config` 칸 — 이름을 여기서 한 번만 적는다(서버와 같은 글자). */
export interface SttConfig {
  /** 호스트까지만. 경로(`/v1/audio/transcriptions`)는 규약이 정한다. */
  endpoint: string;
  model: string;
  /** ★ 단위를 이름에 넣는다 — `timeout` 만 두면 ms 와 초가 갈린다. */
  timeout_sec: number;
}

/** `config` 가 없거나 칸이 비어 있어도 빈 값으로 읽는다 — 지어내지 않는다. */
export function readSttConfig(p: Pick<ExternalApiProvider, 'config'>): SttConfig {
  const c = (p.config ?? {}) as Partial<SttConfig>;
  return {
    endpoint: typeof c.endpoint === 'string' ? c.endpoint : '',
    model: typeof c.model === 'string' ? c.model : '',
    timeout_sec: typeof c.timeout_sec === 'number' ? c.timeout_sec : 30,
  };
}

/** 부를 줄 아는 공급자 목록 — 여기 없는 공급자는 등록해도 아무 데도 안 닿는다. */
export interface ExternalApiAdapter {
  provider_key: string;
  category: ExternalApiCategory;
  default_label: string;
  registered: boolean;
}

export interface ExternalApiList {
  adapters: ExternalApiAdapter[];
  providers: ExternalApiProvider[];
}

export interface ProbeResult {
  verdict: ProbeVerdict;
  http_status: number | null;
  latency_ms: number;
  reason_code: string | null;
  message: string;
}

export async function listExternalApis(): Promise<ExternalApiList> {
  const { data } = await apiClient.get<ExternalApiList>('/admin/external-apis');
  return data;
}

export interface CreateProviderBody {
  provider_key: string;
  label?: string;
  api_key?: string;
  enabled?: boolean;
  sort_order?: number;
  daily_quota?: number | null;
  /** 범주마다 다른 칸 — STT 는 `{ endpoint, model, timeout_sec }`. */
  config?: Record<string, unknown>;
}

export async function createProvider(body: CreateProviderBody): Promise<ExternalApiProvider> {
  const { data } = await apiClient.post<ExternalApiProvider>('/admin/external-apis', body);
  return data;
}

export interface PatchProviderBody {
  label?: string;
  enabled?: boolean;
  sort_order?: number;
  daily_quota?: number | null;
  /** 새 키. 비우면 «안 바꾼다». */
  api_key?: string;
  /** 키를 지운다(그 공급자는 개발용 환경변수 키로 떨어질 수 있다). */
  clear_key?: boolean;
  /** 범주마다 다른 칸 — 보낸 것만 바뀐다. */
  config?: Record<string, unknown>;
}

export async function patchProvider(providerKey: string, body: PatchProviderBody): Promise<ExternalApiProvider> {
  const { data } = await apiClient.patch<ExternalApiProvider>(`/admin/external-apis/${encodeURIComponent(providerKey)}`, body);
  return data;
}

export async function deleteProvider(providerKey: string): Promise<void> {
  await apiClient.delete(`/admin/external-apis/${encodeURIComponent(providerKey)}`);
}

/**
 * 저장 **전** 시험 — 적은 키로 한 번 부른다. 저장하지 않는다.
 * ★ 한도를 태우므로 서버가 사용량에는 적는다. 「넣자마자 안 되는」 일을 막는 자리다.
 */
export async function testKey(providerKey: string, apiKey: string, config?: Record<string, unknown>): Promise<ProbeResult> {
  // ⚠ STT 는 «어디로 부를지»가 config 에 있다 — 그것 없이 시험하면 무엇을 쟀는지 알 수 없다.
  const { data } = await apiClient.post<ProbeResult>('/admin/external-apis/test', {
    provider_key: providerKey, api_key: apiKey, ...(config ? { config } : {}),
  });
  return data;
}

/** 저장 **뒤** 시험 — 저장된 키로 부른다. 결과가 공급자 줄에 남는다. */
export async function testProvider(providerKey: string): Promise<ProbeResult> {
  const { data } = await apiClient.post<ProbeResult>(`/admin/external-apis/${encodeURIComponent(providerKey)}/test`, {});
  return data;
}

/** 회사 층 — 어느 회사가 껐는지. 이 화면은 **읽기로만** 쓴다(끄고 켜는 자리는 뒤로 미뤘다). */
export interface InstitutionSetting {
  institution_id: string;
  code: string | null;
  name: string;
  status: string;
  /** 이 회사가 그 범주를 쓸 수 있나. false 면 `reason` 이 까닭이다(institution_off · no_key …). */
  usable: boolean;
  reason: string;
  /** 이 회사가 실제로 쓰는 공급자. */
  provider_key: string | null;
  /** 회사 층에 행이 없어 카탈로그를 그대로 따르는가. */
  follows_catalog: boolean;
}

export async function listInstitutionSettings(category: ExternalApiCategory): Promise<{ institutions: InstitutionSetting[] }> {
  const { data } = await apiClient.get<{ institutions: InstitutionSetting[] }>('/admin/external-apis/institutions', { params: { category } });
  return data;
}

/* ── 날씨 위치 — 전 회사를 한 번에 (2026-09-21) ───────────────────────── */

/**
 * ★★★ 왜 콘솔용 길이 따로 있나.
 *
 * 8443 의 `GET /weather/grid-settings` 는 **부른 사람의 회사 하나**를 준다. 콘솔은 여러 회사를 보는 화면이라
 * 회사마다 따로 부르면 **사람당 분당 버킷**에 걸리고, 그 길은 BFF 허용 목록에도 없어 여기서는 404 다.
 * 그래서 `/admin/external-apis/weather-grid` 하나로 **전 회사**를 받는다(SYSTEM_ADMIN).
 *
 * ★★ 위치는 **저장된 값이 아니다** — 서버가 주소에서 그때 셈해 준다. 그래서 「확정」 단추가 없고,
 *    관리자가 할 일은 **주소를 고치는 것**뿐이다(그 회사의 8443 회사정보 · 사업장 화면에서).
 */
export type GridLevel = 'sido' | 'sigungu' | 'dong';
export type WeatherGridLocation =
  | { kind: 'ok'; nx: number; ny: number; level: GridLevel; regionName: string }
  /** 주소는 있는데 동·시군구까지 못 맞췄다 — 주소를 **고친다**. */
  | { kind: 'unresolved'; level: GridLevel | null; regionName: string | null }
  /** 주소가 비어 있다 — 주소를 **적는다**. 위와 같은 칸으로 뭉치지 않는다. */
  | { kind: 'no_address' };

export interface WeatherGridPlace {
  name: string;
  address: string | null;
  location: WeatherGridLocation;
}
export interface WeatherGridWorksite extends WeatherGridPlace {
  id: string;
  is_head_office: boolean;
  is_active: boolean;
}
export interface WeatherGridInstitution {
  institution_id: string;
  code: string | null;
  name: string;
  company: WeatherGridPlace;
  worksites: WeatherGridWorksite[];
}

export async function listWeatherGrid(): Promise<{ institutions: WeatherGridInstitution[] }> {
  const { data } = await apiClient.get<{ institutions: WeatherGridInstitution[] }>('/admin/external-apis/weather-grid');
  return data;
}
